import { DateTime } from 'luxon';
import { get, isEmpty, upperFirst } from 'lodash';
import {
  AssetContainerEventType,
  AssetContainerState,
  LuxonDateTimeFormats,
  namesToFullName,
  NovaDate,
  SPOT_STATUSES_RESERVED,
  SpotStatus,
  YardTaskCheckEntity,
  YardTaskCheckLocationOutcome,
  YardTaskFlags,
  YardTaskInventoryGroupType,
  YardTaskStatus
} from '@nova/core';

import type {
  BaseTaskCreateData,
  BaseTaskViewData,
  ChecklistItem,
  CheckTaskApiRequestData,
  CheckTaskApiResponseData,
  CheckTaskCheckAssetContainerView,
  CheckTaskFormData,
  CheckTaskViewData,
  Maybe,
  MoveTaskApiRequestData,
  MoveTaskApiResponseData,
  MoveTaskFormData,
  MoveTaskViewData,
  Nullable,
  TaskApiResponseData,
  TaskViewData,
  TimeInput,
  YardTaskAttachment,
  YardTaskEventData,
  YardTaskInventoryGroupInput,
  YardTaskType,
  YardTaskUser
} from '@nova/core';

type TaskData = { type: 'check' } | { type: 'move' } | { type?: string };

const VALID_YARD_TASK_CHECK_RELATED_ENTITIES = new Set<string>(Object.values(YardTaskCheckEntity));

export function isCheckTask<T extends TaskData>(
  taskData?: Maybe<T>
): taskData is T & { type: 'check' } {
  if (!taskData) {
    return false;
  }

  return taskData.type === 'check';
}

export function isMoveTask<T extends TaskData>(
  taskData?: Maybe<T>
): taskData is T & { type: 'move' } {
  if (!taskData) {
    return false;
  }

  return taskData.type === 'move';
}

export function isTaskUrgent(task?: Maybe<TaskViewData>): boolean {
  return hasTag(task?.tags, YardTaskFlags.URGENT);
}

export function isTaskFlagged(task?: Maybe<TaskViewData>): boolean {
  return hasTag(task?.tags, YardTaskFlags.FLAGGED);
}

export function isTaskIssue(task?: Maybe<TaskViewData>): boolean {
  return hasTag(task?.tags, YardTaskFlags.ISSUE);
}

export function isTaskBlocked(task?: Maybe<TaskViewData>): boolean {
  return hasTag(task?.tags, YardTaskFlags.BLOCKED);
}

export function isTaskStatusCreated(status?: Maybe<string>): boolean {
  return status === YardTaskStatus.CREATED;
}

export function isTaskStatusInProgress(status?: Maybe<string>): boolean {
  return status === YardTaskStatus.IN_PROGRESS;
}

export function isTaskStatusCompleted(status?: Maybe<string>): boolean {
  return status === YardTaskStatus.COMPLETED;
}

export function isTaskStatusCancelled(status?: Maybe<string>): boolean {
  return status === YardTaskStatus.CANCELLED;
}

export function isTaskCreated(task?: Maybe<TaskViewData>): boolean {
  return isTaskStatusCreated(task?.status);
}

export function isTaskInProgress(task?: Maybe<TaskViewData>): boolean {
  return isTaskStatusInProgress(task?.status);
}

export function isTaskCompleted(task?: Maybe<TaskViewData>): boolean {
  return isTaskStatusCompleted(task?.status);
}

export function isTaskCancelled(task?: Maybe<TaskViewData>): boolean {
  return isTaskStatusCancelled(task?.status);
}

export {
  canCancelYardTask,
  canCompleteYardTask,
  canStartYardTask,
  canTransitionYardTaskTo,
  getYardTaskCompleteBlockReason,
  getYardTaskTransitionFeedbackReason,
  getYardTaskTransitionTargets,
  canTransitionYardTask,
  isYardTaskTransitionActionable,
  isYardTaskTransitionTarget,
  YardTaskCompleteBlockReason
} from './yardTaskTransitions';

export type CheckOutcomeChipKind = 'pending' | 'confirmed' | 'missing';

export function getCheckOutcomeChipDisplay(task: CheckTaskViewData): CheckOutcomeChipKind | null {
  if (task.relatedEntity !== YardTaskCheckEntity.AssetContainer) {
    return null;
  }
  const rows = task.checkAssetContainers;
  if (!rows?.length) {
    return 'pending';
  }
  // Re-runs append a new row instead of replacing the prior one, so the chip
  // must reflect only the most recent outcome. Sort ascending by timestamp; for
  // rows without a timestamp fall back to insertion (array) order.
  const latest = rows
    .map((row, index) => ({
      row,
      index,
      ts: row.createDateTime ? Date.parse(row.createDateTime) : Number.NaN
    }))
    .sort((a, b) => {
      const aHasTs = Number.isFinite(a.ts);
      const bHasTs = Number.isFinite(b.ts);
      if (aHasTs && bHasTs && a.ts !== b.ts) {
        return a.ts - b.ts;
      }
      return a.index - b.index;
    })[rows.length - 1].row;
  if (latest.outcome === YardTaskCheckLocationOutcome.MISSING) {
    return 'missing';
  }
  if (latest.outcome === YardTaskCheckLocationOutcome.CONFIRMED) {
    return 'confirmed';
  }
  return 'pending';
}

/**
 * Updates tags in an array by adding and/or removing specific tags
 * Preserves all other tags that aren't being modified
 *
 * @param currentTags - Current array of tag strings
 * @param addTags - Array of tags to add (if not already present)
 * @param removeTags - Array of tags to remove (if present)
 * @returns Updated tags array
 */
export function updateTagsInArray(
  currentTags: string[],
  addTags: string[] = [],
  removeTags: string[] = []
): string[] {
  const tagsArray = mapTags(currentTags);

  // Start with current tags, remove any that should be removed
  const updatedTags = tagsArray.filter(tag => !removeTags.includes(tag));

  // Add any tags that should be added (and aren't already present)
  addTags.forEach(tag => {
    if (!updatedTags.includes(tag)) {
      updatedTags.push(tag);
    }
  });

  return updatedTags;
}

/**
 * Adds a tag to array (ensures uniqueness)
 */
export function addTag(currentTags: string[], ...tags: string[]): string[] {
  return updateTagsInArray(currentTags, tags, []);
}

/**
 * Removes a tag from array
 */
export function removeTag(currentTags: string[], ...tags: string[]): string[] {
  return updateTagsInArray(currentTags, [], tags);
}

export function toggleTag(currentTags: string[], tag: string): string[] {
  if (!Array.isArray(currentTags) || !currentTags.includes(tag)) {
    return addTag(currentTags, tag);
  }

  return removeTag(currentTags, tag);
}

/**
 * Checks if array contains a tag
 */
export function hasTag(tags: Maybe<string[]>, tag: string): boolean {
  return Array.isArray(tags) && tags.includes(tag);
}

/**
 * Compares two tag arrays to determine if they're different
 * @param tags1 - First array of tags
 * @param tags2 - Second array of tags
 * @returns true if the arrays are different, false if they're the same
 */
export function areTagsDifferent(tags1?: Maybe<string[]>, tags2?: Maybe<string[]>): boolean {
  const processTags = (tags?: Maybe<string[]>): string[] => {
    return [...mapTags(tags)].sort();
  };

  const sorted1 = processTags(tags1).join(',');
  const sorted2 = processTags(tags2).join(',');

  return sorted1 !== sorted2;
}

/**
 * Gets the label for a task status
 */
export function getTaskStatusLabel(
  taskStatus?: YardTaskStatus
): 'To Do' | 'In Progress' | 'Completed' | 'Cancelled' {
  switch (taskStatus) {
    case YardTaskStatus.CREATED:
      return 'To Do';
    case YardTaskStatus.IN_PROGRESS:
      return 'In Progress';
    case YardTaskStatus.COMPLETED:
      return 'Completed';
    case YardTaskStatus.CANCELLED:
      return 'Cancelled';
    default:
      return 'To Do';
  }
}

/**
 * Gets the icon for a task status
 */
export function getTaskStatusIcon(taskStatus?: YardTaskStatus): string {
  switch (taskStatus) {
    case YardTaskStatus.CREATED:
      return 'mdi-progress-helper';
    case YardTaskStatus.IN_PROGRESS:
      return 'mdi-progress-clock';
    case YardTaskStatus.COMPLETED:
      return 'mdi-check-circle';
    case YardTaskStatus.CANCELLED:
      return 'mdi-close-circle';
    default:
      return 'mdi-circle-outline';
  }
}

/**
 * Gets the icon/text color for a task status
 */
export function getTaskStatusColor(taskStatus?: YardTaskStatus): string {
  switch (taskStatus) {
    case YardTaskStatus.IN_PROGRESS:
      return 'primary-100';
    case YardTaskStatus.COMPLETED:
      return 'success-100';
    case YardTaskStatus.CANCELLED:
      return 'error-60';
    case YardTaskStatus.CREATED:
    default:
      return 'neutral-80';
  }
}

/**
 * Alias for getTaskStatusColor just so we don't break the useYardTaskPresenter
 */
export { getTaskStatusColor as getTaskStatusIconColor };

/**
 * Gets the background color for a task status
 */
export function getTaskStatusBackgroundColor(taskStatus?: YardTaskStatus): string {
  switch (taskStatus) {
    case YardTaskStatus.IN_PROGRESS:
      return 'primary-20';
    case YardTaskStatus.COMPLETED:
      return 'success-20';
    case YardTaskStatus.CANCELLED:
      return 'error-20';
    case YardTaskStatus.CREATED:
    default:
      return 'neutral-20';
  }
}

/**
 * Formats a task number with leading zeros
 */
export function formatTaskNumber(taskNumber?: Maybe<string | number>): Nullable<string> {
  if (!taskNumber) {
    return null;
  }

  return String(taskNumber).padStart(3, '0');
}

/**
 * Formats a person's name from first and last name
 */
export function formatPersonName(
  person?: Maybe<{ firstName?: Maybe<string>; lastName?: Maybe<string> }>
): Nullable<string> {
  if (!person) {
    return null;
  }

  const { firstName, lastName } = {
    firstName: person?.firstName || '',
    lastName: person?.lastName || ''
  };
  return namesToFullName(firstName, lastName);
}

/**
 * Formats a task category name by replacing dashes with spaces and capitalizing words
 * @param category - Task category string (e.g., "pre-trip-inspection")
 * @param taskType - Fallback task type if category is not available
 * @returns Formatted category name (e.g., "Pre Trip Inspection") or formatted task type
 */
export function formatTaskCategoryName(
  category?: Maybe<string>,
  taskType?: Maybe<YardTaskType>
): string {
  if (category) {
    return category.replace(/-/g, ' ').replace(/\b\w/g, (letter: string) => letter.toUpperCase());
  }

  return taskType ? upperFirst(taskType) : 'No Type';
}

/**
 * Standardized date formatting utility for task dates
 * @param date - ISO date string or null
 * @param format - Optional format string (default: 'MMM d, yyyy @ h:mm a')
 * @param timezone - Optional timezone (default: 'utc')
 * @returns Formatted date string or null
 */
export function formatTaskDate(
  date: Nullable<string>,
  format: string = 'MMM d, yyyy @ h:mm a',
  timezone: string = 'utc'
): Nullable<string> {
  if (!date) {
    return null;
  }

  try {
    return new NovaDate(date).format(format, { tz: timezone });
  } catch (error) {
    console.error('Error formatting date:', error);
    return null;
  }
}

/**
 * Formats a date/time as relative time (e.g., "2 hours ago", "yesterday", "3 days ago")
 * @param dateTime - ISO date string
 * @param timezone - Optional timezone (default: 'utc')
 * @returns Relative time string or formatted date if too old
 */
export function formatRelativeTime(dateTime: string, timezone: string = 'utc'): string {
  if (!dateTime) {
    return '';
  }

  try {
    const date = DateTime.fromISO(dateTime).setZone(timezone);
    if (!date.isValid) {
      return '';
    }

    const now = DateTime.now().setZone(timezone);
    const diff = now.diff(date, ['days', 'hours', 'minutes']);

    const days = Math.floor(diff.days);
    const hours = Math.floor(diff.hours);
    const minutes = Math.floor(diff.minutes);

    if (days > 7) {
      // If older than a week, return formatted date
      return formatTaskDate(dateTime, 'MMM d, yyyy', timezone) || '';
    }

    if (days > 0) {
      return days === 1 ? 'yesterday' : `${days} days ago`;
    }

    if (hours > 0) {
      return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
    }

    if (minutes > 0) {
      return minutes === 1 ? '1 minute ago' : `${minutes} minutes ago`;
    }

    return 'just now';
  } catch (error) {
    console.error('Error formatting relative time:', error);
    return '';
  }
}

/**
 * Builds the due date time by combining date and time
 * @param dueByDate - ISO date string
 * @param dueByTime - Time
 * @param warehouseTimezone - Warehouse timezone
 * @returns ISO date string with time, or null if no date provided
 */
export function mapDueDateTime(
  dueByDate?: string,
  dueByTime?: TimeInput,
  warehouseTimezone?: string
): Nullable<string> {
  if (!dueByDate) {
    return null;
  }

  const newDateTimeString = [
    DateTime.fromISO(dueByDate).toFormat('yyyy-MM-dd'),
    dueByTime?.time || '11:59',
    dueByTime?.amPm || 'PM'
  ].join(' ');

  const newDateTime = DateTime.fromFormat(
    newDateTimeString,
    `${LuxonDateTimeFormats.DateDashed} ${LuxonDateTimeFormats.Extended12HrTimePadding} a`
  ).setZone(warehouseTimezone, { keepLocalTime: true });

  return newDateTime?.isValid ? newDateTime.toISO() : null;
}

export function getSortableDueDateTimestamp(task: TaskApiResponseData): number | null {
  const { dueDateTime } = task;

  if (!dueDateTime) {
    return null;
  }

  const parsed = Date.parse(dueDateTime);
  return Number.isNaN(parsed) ? null : parsed;
}

export function sortTasksByDueDateTime(items: TaskApiResponseData[]): TaskApiResponseData[] {
  if (!Array.isArray(items)) {
    return [];
  }

  return [...items].sort((a, b) => {
    const aDueTimestamp = getSortableDueDateTimestamp(a);
    const bDueTimestamp = getSortableDueDateTimestamp(b);

    if (aDueTimestamp === null && bDueTimestamp === null) {
      return (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER);
    }

    if (aDueTimestamp === null) {
      return 1;
    }

    if (bDueTimestamp === null) {
      return -1;
    }

    if (aDueTimestamp !== bDueTimestamp) {
      return aDueTimestamp - bDueTimestamp;
    }

    return (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER);
  });
}

/**
 * Maps checklist items from form format to API format.
 * The mapper assumes that the checklist is an array of objects with a description and complete property.
 * If the complete property is not present, it defaults to false.
 * @param checklist - Form checklist items
 * @returns API checklist format
 */
export function mapChecklist(checklist?: ChecklistItem[]): ChecklistItem[] {
  if (!Array.isArray(checklist)) {
    return [];
  }

  if (checklist.length === 0) {
    return [];
  }

  /**
   * At this point, the checklist has been validated, so the reduce is just to
   * remove a possibly trailing empty item
   */
  return checklist.reduce((acc, item) => {
    if (isEmpty(item.description)) {
      return acc;
    }

    return [
      ...acc,
      {
        complete: item.complete || false,
        description: item.description
      }
    ];
  }, [] as ChecklistItem[]);
}

/**
 * Maps inventory-group selections from form state to API request format.
 * `spotArea` groups carry the spotAreaId in `value`; `missing` / `unassigned`
 * carry no value. Invalid/empty entries are dropped and duplicates removed.
 * @param groups - Inventory groups from form state
 * @returns Normalized inventory groups for the API payload
 */
export function mapInventoryGroups(
  groups?: Maybe<YardTaskInventoryGroupInput[]>
): YardTaskInventoryGroupInput[] {
  if (!Array.isArray(groups)) {
    return [];
  }

  const validTypes = new Set<YardTaskInventoryGroupType>(Object.values(YardTaskInventoryGroupType));
  const seen = new Set<string>();

  return groups.reduce((acc, group) => {
    if (!group || !validTypes.has(group.type)) {
      return acc;
    }

    const isSpotArea = group.type === YardTaskInventoryGroupType.SpotArea;
    const value = isSpotArea ? group.value || null : null;

    if (isSpotArea && !value) {
      return acc;
    }

    const key = `${group.type}:${value ?? ''}`;
    if (seen.has(key)) {
      return acc;
    }
    seen.add(key);

    return [...acc, isSpotArea ? { type: group.type, value } : { type: group.type }];
  }, [] as YardTaskInventoryGroupInput[]);
}

/**
 * Maps related entity and ID based on subject type
 * @param subject - Subject type
 * @param spot - Spot ID
 * @param area - Area ID
 * @param asset - Asset ID
 * @param warehouseId - Warehouse ID (used as relatedId for warehouse-scoped inventory checks)
 * @returns Object with relatedEntity and relatedId
 */
export function mapRelatedEntity(
  subject: string,
  spot?: string,
  area?: string,
  asset?: string,
  warehouseId?: string
) {
  if (subject !== '' && !VALID_YARD_TASK_CHECK_RELATED_ENTITIES.has(subject)) {
    throw new Error(`Invalid relatedEntity: ${subject}`);
  }

  const relatedEntity = subject;
  let relatedId: Nullable<string> = null;

  if (relatedEntity === YardTaskCheckEntity.Spot) {
    relatedId = spot || null;
  } else if (relatedEntity === YardTaskCheckEntity.SpotArea) {
    relatedId = area || null;
  } else if (relatedEntity === YardTaskCheckEntity.Warehouse) {
    relatedId = warehouseId || null;
  } else {
    relatedId = asset || null;
  }

  return { relatedEntity, relatedId };
}

/**
 * Maps tags array to API format (for create/edit flow)
 * @param tags - Array of tag strings
 * @returns Array of tags (ensures it's an array)
 */
export function mapTags(tags?: Maybe<string[]>): string[] {
  return Array.isArray(tags) ? tags : [];
}

/**
 * Transforms due date time from API format to form state format
 * @param dueDateTime - ISO date string from API
 * @param warehouseTimezone - Warehouse timezone
 * @returns Object with dueByDate and dueByTime
 */
export function transformDueDatePayloadToState(
  dueDateTime: string,
  warehouseTimezone: string
): {
  dueByDate: string;
  dueByTime: TimeInput;
} {
  if (!dueDateTime) {
    return {
      dueByDate: '',
      dueByTime: {
        time: '',
        amPm: 'pm'
      }
    };
  }

  const date = DateTime.fromISO(dueDateTime).setZone(warehouseTimezone);
  const dueByDate = date.isValid ? date.toFormat('yyyy-MM-dd') : '';
  const dueByTime = {
    time: date.isValid ? date.toFormat('hh:mm') : '',
    amPm: date.isValid ? (date.hour >= 12 ? 'pm' : 'am') : 'pm'
  };

  return {
    dueByDate,
    dueByTime
  };
}

/**
 * Maps the task detail attachments payload from `documents` endpoint to view data.
 * Shared by every task type — the detail endpoint joins them for `YardTask` regardless
 * of the child type.
 * @param apiResponse - API response data (check or move)
 * @returns Attachment documents for view data, or undefined when the payload omits them
 */
function mapTaskAttachments(apiResponse: TaskApiResponseData): YardTaskAttachment[] | undefined {
  const attachments = get(apiResponse, 'documents');

  if (!Array.isArray(attachments)) {
    return undefined;
  }

  return attachments.map((attachment: YardTaskAttachment) => ({
    id: attachment.id,
    name: attachment.name,
    mimetype: attachment.mimetype,
    url: attachment.url,
    createDateTime: attachment.createDateTime,
    uploadedBy: attachment.uploadedBy
  }));
}

/**
 * Extracts common base properties from API response to view data
 * @param apiResponse - API response data (check or move)
 * @returns Common base properties for view data
 */
function extractBaseViewDataFromApiResponse(apiResponse: TaskApiResponseData): BaseTaskViewData & {
  id: Nullable<string>;
  status: string;
  order: Nullable<number>;
  refNum: Nullable<number>;
  events: YardTaskEventData[];
} {
  return {
    id: get(apiResponse, 'id') || null,
    status: get(apiResponse, 'status') || '',
    description: get(apiResponse, 'description') || '',
    order: get(apiResponse, 'order') || null,
    refNum: get(apiResponse, 'refNum') || null,
    createdAt: get(apiResponse, 'createDateTime') || null,
    createdBy: get(apiResponse, 'createdBy') || null,
    tags: get(apiResponse, 'tags') || [],
    events: get(apiResponse, 'events') || [],
    attachments: mapTaskAttachments(apiResponse),

    assignedTo: get(apiResponse, 'assignedTo') || null,
    assignedBy: get(apiResponse, 'assignedBy') || null,
    assignedDateTime: get(apiResponse, 'assignedDateTime') || null,
    dueDateTime: get(apiResponse, 'dueDateTime') || null,
    lastChangedBy: get(apiResponse, 'lastChangedBy') || null,
    lastChangedDateTime: get(apiResponse, 'lastChangedDateTime') || null
  };
}

/**
 * Extracts common base properties from view data to form data
 * @param viewData - View data (check or move)
 * @param warehouseTimezone - Warehouse timezone
 * @returns Common base properties for form data
 */
function extractBaseFormDataFromViewData(
  viewData: TaskViewData,
  warehouseTimezone: string
): Pick<BaseTaskCreateData, 'description' | 'assignee' | 'dueByDate' | 'dueByTime' | 'tags'> & {
  id?: Maybe<string>;
  order?: Maybe<number>;
  refNum?: Maybe<number>;
  createdAt?: string;
  createdBy?: YardTaskUser;
  status?: string;
} {
  const dueDateState = viewData.dueDateTime
    ? transformDueDatePayloadToState(viewData.dueDateTime, warehouseTimezone)
    : { dueByDate: '', dueByTime: { time: '', amPm: 'pm' } };

  return {
    id: viewData.id,
    order: viewData.order,
    refNum: viewData.refNum,
    description: viewData.description,
    assignee: viewData.assignedTo?.id || null,
    dueByDate: dueDateState.dueByDate || undefined,
    dueByTime: dueDateState.dueByTime || undefined,
    tags: viewData.tags,
    createdAt: viewData.createdAt || undefined,
    createdBy: viewData.createdBy || undefined,
    status: viewData.status
  };
}

/**
 * Transforms check task API response to view data format (for presentation)
 * @param apiResponse - API response data
 * @returns Check task view data
 */
export function transformCheckTaskApiResponseToViewData(
  apiResponse: CheckTaskApiResponseData
): CheckTaskViewData {
  const relatedEntity = get(apiResponse, 'child.relatedEntity');
  const relatedId = get(apiResponse, 'child.relatedId');
  const relatedObject = get(apiResponse, 'child.relatedObject');
  const baseData = extractBaseViewDataFromApiResponse(apiResponse);
  const rawContainers = get(apiResponse, 'child.checkAssetContainers');
  const checkAssetContainers: CheckTaskCheckAssetContainerView[] | undefined = Array.isArray(
    rawContainers
  )
    ? rawContainers
        .filter(
          (
            row
          ): row is {
            outcome: YardTaskCheckLocationOutcome;
            createDateTime?: unknown;
            assetContainerId?: unknown;
          } => {
            if (!row || typeof row !== 'object' || Array.isArray(row)) {
              return false;
            }
            const o = (row as { outcome?: unknown }).outcome;
            return (
              o != null &&
              (
                Object.values(YardTaskCheckLocationOutcome) as YardTaskCheckLocationOutcome[]
              ).includes(o as YardTaskCheckLocationOutcome)
            );
          }
        )
        .map(row => ({
          outcome: row.outcome,
          // Defensive coercion: downstream code (`getCheckOutcomeChipDisplay`,
          // `pickLatestCheckOutcome`) calls `Date.parse` on this and passes the asset id
          // through `Map.get` — both would silently misbehave if the API ever returned a
          // non-string. Anything that isn't a string collapses to `null`.
          createDateTime: typeof row.createDateTime === 'string' ? row.createDateTime : null,
          assetContainerId: typeof row.assetContainerId === 'string' ? row.assetContainerId : null
        }))
    : undefined;

  const rawInventoryGroups = get(apiResponse, 'child.inventoryGroups');
  const inventoryGroups = Array.isArray(rawInventoryGroups)
    ? mapInventoryGroups(rawInventoryGroups as YardTaskInventoryGroupInput[])
    : undefined;

  return {
    ...baseData,
    type: 'check',
    category: get(apiResponse, 'child.category') || '',
    subject: relatedEntity || '',
    checklist: (get(apiResponse, 'child.checklist') || []).map((item: any) => ({
      description: item.description,
      complete: item.complete
    })),
    relatedEntity: relatedEntity || undefined,
    relatedId: relatedId || null,
    relatedObject: relatedObject || null,
    checkId: get(apiResponse, 'child.id') || undefined,
    warehouseId: get(apiResponse, 'warehouseId') || undefined,
    checkAssetContainers,
    inventoryGroups,
    events: baseData.events || []
  };
}

/**
 * Transforms move task API response to view data format (for presentation)
 * @param apiResponse - API response data
 * @returns Move task view data
 */
export function transformMoveTaskApiResponseToViewData(
  apiResponse: MoveTaskApiResponseData
): MoveTaskViewData {
  const baseData = extractBaseViewDataFromApiResponse(apiResponse);

  return {
    ...baseData,
    type: 'move',
    originSpot: get(apiResponse, 'child.originSpot') || null,
    destinationSpot: get(apiResponse, 'child.destinationSpot') || null,
    assetContainer: get(apiResponse, 'child.assetContainer') || null,
    originSpotArea: get(apiResponse, 'child.originSpotArea') || null,
    destinationSpotArea: get(apiResponse, 'child.destinationSpotArea') || null,
    events: baseData.events || []
  };
}

/**
 * Transforms task API response to view data format (for presentation)
 * @param apiResponse - API response data
 * @param warehouseTimezone - Warehouse timezone
 * @returns View data object (CheckTaskViewData or MoveTaskViewData)
 */
export function transformTaskApiResponseToViewData(
  apiResponse: Maybe<TaskApiResponseData>
): Nullable<TaskViewData> {
  if (!apiResponse) {
    return null;
  }

  if (isCheckTask(apiResponse)) {
    return transformCheckTaskApiResponseToViewData(apiResponse);
  }

  if (isMoveTask(apiResponse)) {
    return transformMoveTaskApiResponseToViewData(apiResponse);
  }

  return null;
}

/**
 * Transforms check task view data to form data format (for forms)
 * @param viewData - View data
 * @param warehouseTimezone - Warehouse timezone
 * @returns Check task form data
 */
export function transformCheckTaskViewDataToFormData(
  viewData: CheckTaskViewData,
  warehouseTimezone: string
): CheckTaskFormData {
  const relatedEntity = viewData.relatedEntity || viewData.subject || '';
  const relatedId = viewData.relatedId ?? null;
  const baseData = extractBaseFormDataFromViewData(viewData, warehouseTimezone);

  return {
    ...baseData,
    type: 'check',
    checklist: viewData.checklist,
    subject: viewData.subject,
    category: viewData.category,
    spot: relatedEntity === YardTaskCheckEntity.Spot ? relatedId : null,
    area: relatedEntity === YardTaskCheckEntity.SpotArea ? relatedId : null,
    asset: relatedEntity === YardTaskCheckEntity.AssetContainer ? relatedId : null,
    inventoryGroups:
      relatedEntity === YardTaskCheckEntity.Warehouse ? (viewData.inventoryGroups ?? []) : undefined
  };
}

/**
 * Transforms move task view data to form data format (for forms)
 * @param viewData - View data
 * @param warehouseTimezone - Warehouse timezone
 * @returns Move task form data
 */
export function transformMoveTaskViewDataToFormData(
  viewData: MoveTaskViewData,
  warehouseTimezone: string
): MoveTaskFormData {
  const baseData = extractBaseFormDataFromViewData(viewData, warehouseTimezone);

  return {
    ...baseData,
    type: 'move',
    asset: viewData.assetContainer?.id || null,
    originSpot: viewData.originSpot?.id || null,
    destinationSpot: viewData.destinationSpot?.id || null
  };
}

/**
 * Transforms task view data to form data format (for forms)
 * @param viewData - View data
 * @returns Form data object (CheckTaskFormData or MoveTaskFormData)
 */
export function transformTaskViewDataToFormData(
  viewData: Maybe<TaskViewData>,
  warehouseTimezone: string
): Nullable<CheckTaskFormData | MoveTaskFormData> {
  if (!viewData) {
    return null;
  }

  if (isCheckTask(viewData)) {
    return transformCheckTaskViewDataToFormData(viewData, warehouseTimezone);
  }

  if (isMoveTask(viewData)) {
    return transformMoveTaskViewDataToFormData(viewData, warehouseTimezone);
  }

  return null;
}

/**
 * Transforms check task API response to form data format (for create/edit forms)
 * @param apiResponse - API response data
 * @param warehouseTimezone - Warehouse timezone
 * @returns Check task form data
 */
export function transformCheckTaskApiResponseToFormData(
  apiResponse: CheckTaskApiResponseData,
  warehouseTimezone: string
): CheckTaskFormData {
  const viewData = transformCheckTaskApiResponseToViewData(apiResponse);
  return transformCheckTaskViewDataToFormData(viewData, warehouseTimezone);
}

/**
 * Transforms move task API response to form data format (for create/edit forms)
 * @param apiResponse - API response data
 * @param warehouseTimezone - Warehouse timezone
 * @returns Move task form data
 */
export function transformMoveTaskApiResponseToFormData(
  apiResponse: MoveTaskApiResponseData,
  warehouseTimezone: string
): MoveTaskFormData {
  const viewData = transformMoveTaskApiResponseToViewData(apiResponse);
  return transformMoveTaskViewDataToFormData(viewData, warehouseTimezone);
}

/**
 * Transforms task API response to form data format (for create/edit forms)
 * @param apiResponse - API response data
 * @param warehouseTimezone - Warehouse timezone
 * @returns Form data object (CheckTaskFormData or MoveTaskFormData)
 */
export function transformTaskApiResponseToFormData(
  apiResponse: Maybe<TaskApiResponseData>,
  warehouseTimezone: string
): Nullable<CheckTaskFormData | MoveTaskFormData> {
  if (!apiResponse) {
    return null;
  }

  if (isCheckTask(apiResponse)) {
    return transformCheckTaskApiResponseToFormData(apiResponse, warehouseTimezone);
  }

  if (isMoveTask(apiResponse)) {
    return transformMoveTaskApiResponseToFormData(apiResponse, warehouseTimezone);
  }

  return null;
}

/**
 * Transforms base/common task form data to API request payload format
 * Only includes properties that were provided in the form data
 * @param taskData - Form task data (check or move, partial)
 * @param options - Options containing warehouseId (optional) and warehouseTimezone
 * @returns Partial API request payload with only common properties
 */
export function transformBaseTaskFormDataToApiRequestData(
  taskData: Partial<CheckTaskFormData> | Partial<MoveTaskFormData>,
  options?: { warehouseId?: string; warehouseTimezone?: string }
): Partial<CheckTaskApiRequestData> | Partial<MoveTaskApiRequestData> {
  if (!taskData) {
    throw new Error('No task data available');
  }

  const { warehouseId, warehouseTimezone } = options || {};
  const payload: Partial<CheckTaskApiRequestData> | Partial<MoveTaskApiRequestData> = {};

  if (taskData.assignee !== undefined) {
    payload.assignedTo = taskData.assignee || null;
  }

  if (taskData.description !== undefined) {
    payload.description = taskData.description || '';
  }

  if (taskData.tags !== undefined) {
    payload.tags = mapTags(taskData.tags);
  }

  const hasDueByDate = 'dueByDate' in taskData;
  const hasDueByTime = 'dueByTime' in taskData;

  if (hasDueByDate || hasDueByTime) {
    payload.dueDateTime = mapDueDateTime(taskData.dueByDate, taskData.dueByTime, warehouseTimezone);
  }

  if (warehouseId) {
    payload.warehouseId = warehouseId;
  }

  if (taskData.type) {
    payload.type = taskData.type;
  }

  if (taskData.status) {
    payload.status = taskData.status;
  }

  return payload;
}

/**
 * Transforms check task form data to API request payload format
 * @param taskData - Form task data
 * @param options - Options containing warehouseId (optional) and warehouseTimezone
 * @returns API request payload (partial, only includes provided properties)
 */
export function transformCheckTaskFormDataToApiRequestData(
  taskData: Partial<CheckTaskFormData>,
  options?: { warehouseId?: string; warehouseTimezone?: string }
): Partial<CheckTaskApiRequestData> {
  if (!taskData) {
    throw new Error('No task data available');
  }

  const payload = transformBaseTaskFormDataToApiRequestData(
    taskData,
    options
  ) as Partial<CheckTaskApiRequestData>;

  const hasChecklist = taskData.checklist !== undefined;
  const hasCategory = taskData.category !== undefined;
  const hasSubject = taskData.subject !== undefined;
  const hasSpot = taskData.spot !== undefined;
  const hasArea = taskData.area !== undefined;
  const hasAsset = taskData.asset !== undefined;
  const hasInventoryGroups = taskData.inventoryGroups !== undefined;

  if (
    hasChecklist ||
    hasCategory ||
    hasSubject ||
    hasSpot ||
    hasArea ||
    hasAsset ||
    hasInventoryGroups
  ) {
    const checklist = hasChecklist ? mapChecklist(taskData.checklist) : [];
    const { relatedEntity, relatedId } = mapRelatedEntity(
      taskData.subject || '',
      hasSpot ? taskData.spot : undefined,
      hasArea ? taskData.area : undefined,
      hasAsset ? taskData.asset : undefined,
      options?.warehouseId
    );

    const child: Partial<CheckTaskApiRequestData['child']> = {};

    if (hasChecklist) {
      child.checklist = checklist;
    }

    // Inventory groups only apply to warehouse-scoped checks; never emit them for
    // asset/spot/area subjects even if the form left an (empty) array behind.
    if (hasInventoryGroups && relatedEntity === YardTaskCheckEntity.Warehouse) {
      child.inventoryGroups = mapInventoryGroups(taskData.inventoryGroups);
    }

    if (hasCategory && taskData.category !== undefined && taskData.category !== null) {
      const trimmed =
        typeof taskData.category === 'string' ? taskData.category.trim() : taskData.category;
      if (typeof trimmed === 'string' && trimmed.length > 0) {
        child.category = trimmed;
      }
    }

    // Only set both relatedEntity and relatedId if both are present (they must be paired)
    if (relatedEntity && relatedId !== null) {
      child.relatedEntity = relatedEntity as YardTaskCheckEntity;
      child.relatedId = relatedId;
    }

    if (!isEmpty(child)) {
      payload.child = child as CheckTaskApiRequestData['child'];
    }
  }

  return payload;
}

/**
 * Transforms move task form data to API request payload format
 * @param taskData - Form task data
 * @param options - Additional options
 * @returns API request payload (partial, only includes provided properties)
 */
export function transformMoveTaskFormDataToApiRequestData(
  taskData: Partial<MoveTaskFormData>,
  options?: { warehouseId?: string; warehouseTimezone?: string }
): Partial<MoveTaskApiRequestData> {
  if (!taskData) {
    throw new Error('No task data available');
  }

  const payload = transformBaseTaskFormDataToApiRequestData(
    taskData,
    options
  ) as Partial<MoveTaskApiRequestData>;

  const hasAsset = taskData.asset !== undefined;
  const hasOriginSpot = taskData.originSpot !== undefined;
  const hasDestinationSpot = taskData.destinationSpot !== undefined;

  if (hasAsset || hasOriginSpot || hasDestinationSpot) {
    const child: Partial<MoveTaskApiRequestData['child']> = {};

    if (hasAsset) {
      child.assetContainerId = taskData.asset ?? null;
    }

    if (hasOriginSpot) {
      child.originSpotId = taskData.originSpot ?? null;
    }

    if (hasDestinationSpot) {
      child.destinationSpotId = taskData.destinationSpot ?? null;
    }

    if (!isEmpty(child)) {
      payload.child = child as MoveTaskApiRequestData['child'];
    }
  }

  return payload;
}

/**
 * Transforms form data to API request payload format
 * @param taskData - Form task data (check or move)
 * @param options - Additional options
 * @returns API request payload (partial) or null if invalid
 */
/**
 * Returns the Vuetify color for an asset container event type (status).
 * Single source of truth — mirrors useYard.js getAssetContainerStatusColor.
 */
export function getAssetContainerStatusColor(
  status: AssetContainerEventType | string
): string | undefined {
  if (
    status === AssetContainerEventType.Loading ||
    status === AssetContainerEventType.LoadingComplete
  ) {
    return 'warning';
  }
  if (
    status === AssetContainerEventType.Unloading ||
    status === AssetContainerEventType.UnloadingComplete
  ) {
    return 'info';
  }
  if (status === AssetContainerEventType.ReadyForPickup) {
    return 'success';
  }
  if (status === AssetContainerEventType.InTransit) {
    return 'grey';
  }
  if (status === AssetContainerEventType.Inoperable || status === AssetContainerEventType.Missing) {
    return 'error';
  }
  return undefined;
}

/**
 * Returns the MDI icon for an asset container event type (status).
 * Single source of truth — mirrors useYard.js getAssetContainerStatusIcon.
 */
export function getAssetContainerStatusIcon(status: AssetContainerEventType | string): string {
  if (
    status === AssetContainerEventType.LoadingComplete ||
    status === AssetContainerEventType.UnloadingComplete
  ) {
    return 'mdi-check-bold';
  }
  if (status === AssetContainerEventType.Loading) {
    return 'mdi-transfer-down';
  }
  if (status === AssetContainerEventType.Unloading) {
    return 'mdi-transfer-up';
  }
  if (status === AssetContainerEventType.ReadyForPickup) {
    return 'mdi-check-circle';
  }
  if (status === AssetContainerEventType.Departed) {
    return 'mdi-truck-fast-outline';
  }
  if (status === AssetContainerEventType.InTransit) {
    return 'mdi-transit-connection-variant';
  }
  if (status === AssetContainerEventType.Inoperable) {
    return 'mdi-alert-octagon';
  }
  if (status === AssetContainerEventType.Missing) {
    return 'mdi-map-marker-off';
  }
  return 'mdi-progress-clock';
}

/**
 * Returns the MDI icon for a spot occupancy status.
 * Single source of truth for Helios and Circe.
 */
export function getSpotStatusIcon(status: SpotStatus | string): string {
  if (status === SpotStatus.OPEN) {
    return 'mdi-check';
  }
  if (status === SpotStatus.CLOSED) {
    return 'mdi-cancel';
  }
  if (status === SpotStatus.ASSIGNED) {
    return 'mdi-view-column';
  }
  if (status === SpotStatus.IN_TRANSIT) {
    return 'mdi-transit-connection-variant';
  }
  if (SPOT_STATUSES_RESERVED.includes(status as SpotStatus)) {
    return 'mdi-lock-clock';
  }
  return 'mdi-help-circle-outline';
}

/**
 * Returns the Vuetify color for a spot occupancy status.
 * Single source of truth for Helios and Circe.
 */
export function getSpotStatusColor(status: SpotStatus | string): string | undefined {
  if (status === SpotStatus.OPEN) {
    return 'success';
  }
  if (status === SpotStatus.CLOSED) {
    return 'error';
  }
  if (SPOT_STATUSES_RESERVED.includes(status as SpotStatus)) {
    return 'neutral-90';
  }
  if (status === SpotStatus.ASSIGNED) {
    return 'neutral-90';
  }
  if (status === SpotStatus.IN_TRANSIT) {
    return 'warning';
  }
  return undefined;
}

/**
 * Returns the MDI icon for an asset container state.
 * Single source of truth — mirrors useYard.js getAssetContainerStateIcon.
 */
export function getAssetContainerStateIcon(state: AssetContainerState | string): string {
  if (state === AssetContainerState.Full) {
    return 'mdi-circle';
  }
  if (state === AssetContainerState.PartiallyLoaded) {
    return 'mdi-circle-half-full';
  }
  return 'mdi-circle-outline';
}

export function transformFormDataToApiRequestData(
  taskData?: Maybe<Partial<CheckTaskFormData> | Partial<MoveTaskFormData>>,
  options?: { warehouseId?: string; warehouseTimezone?: string }
): Nullable<Partial<CheckTaskApiRequestData> | Partial<MoveTaskApiRequestData>> {
  if (!taskData) {
    return null;
  }

  if (isCheckTask(taskData)) {
    return transformCheckTaskFormDataToApiRequestData(taskData, options);
  }

  if (isMoveTask(taskData)) {
    return transformMoveTaskFormDataToApiRequestData(taskData, options);
  }

  // If we can't detect the task type, use base transformation (for partial updates like tags only)
  return transformBaseTaskFormDataToApiRequestData(taskData, options);
}
